import { Button, Modal, Spinner } from '@crm/ui';
import { FileDown, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { renderScreenPdf } from '@/lib/pdf';
import { withPrintMode } from '@/lib/print-mode';

/**
 * Abre a janela de impressao do navegador. Quem monta a folha e o CSS de
 * impressao do `globals.css`, que esconde menu, cabecalho e os proprios
 * botoes.
 */
export function PrintButton({ label = 'Imprimir' }: { label?: string }) {
  return (
    <Button
      variant="outline"
      className="print:hidden"
      // A lista paginada abre inteira antes da janela de impressao: no papel
      // ninguem clica em "Ver mais".
      onClick={() => withPrintMode(() => window.print())}
      title={label}
      aria-label={label}
    >
      <Printer className="h-4 w-4" />
      {/* No celular a impressora fala por si: o rotulo so gastava a largura
          que o botao seguinte precisava para caber na mesma linha. */}
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}

/**
 * Gera o PDF do relatorio inteiro - o mesmo conteudo da impressao - e mostra
 * no visualizador dentro de uma modal, de onde da para baixar.
 */
export function ExportPdfButton({
  title,
  label = 'Exportar PDF',
  /** Versao curta do rotulo, usada no celular. */
  shortLabel = 'PDF',
}: {
  title: string;
  label?: string;
  shortLabel?: string;
}) {
  const [isOpen, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // O arquivo vive em memoria: sem revogar, cada exportacao deixa lixo.
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  async function exportar() {
    setOpen(true);
    setError(null);
    setUrl(null);

    try {
      // Espera a modal pintar antes de fotografar a tela que esta atras dela,
      // e abre as listas paginadas para o PDF nao sair cortado.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      setUrl(await withPrintMode(renderScreenPdf));
    } catch (falha) {
      setError(falha instanceof Error ? falha.message : 'Nao foi possivel gerar o PDF.');
    }
  }

  function fechar() {
    setOpen(false);
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
  }

  return (
    <>
      <Button
        variant="outline"
        className="print:hidden"
        onClick={exportar}
        title={label}
        aria-label={label}
      >
        <FileDown className="h-4 w-4" />
        <span className="sm:hidden">{shortLabel}</span>
        <span className="hidden sm:inline">{label}</span>
      </Button>

      <Modal
        open={isOpen}
        onClose={fechar}
        size="full"
        compactHeader
        flushBody
        // Sem barra de acoes: o proprio visualizador do navegador ja traz
        // baixar e imprimir, e o X do cabecalho fecha.
        title={`Exportar ${title}`}
      >
        {error ? (
          <p className="py-10 text-center text-sm text-destructive">{error}</p>
        ) : url ? (
          // O visualizador ocupa a modal inteira: a moldura dele ja e a folha.
          <iframe src={url} title={`Previa do PDF - ${title}`} className="h-full w-full border-0" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <Spinner className="h-6 w-6" />
            <p className="text-sm">Montando o PDF do relatorio...</p>
          </div>
        )}
      </Modal>
    </>
  );
}
